import { pick, prop, pipe, map } from 'ramda';
import { useMemo, useCallback, ReactNode } from 'react';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';
import { useDynamicReducerSelector } from '../store';
import style from '../index.scss';
import { getTableAccordionDataCreator } from '@theme/js/components/common/Table/accordionFactory/tableDataCreator';
import { getHeaderCellsCreator } from '../cellMappers/getHeaderCellsCreator';
import { getFormattedData } from '../formatters/getFormattedData';
import { useShowTermsPopup } from '@core/js/components/main/BonusHistory/hooks';
import { getRowCellsCreator } from '@theme/js/components/common/Table/accordionFactory/cellMappers/getRowCellsCreator';
import { IBonusDetailsItem } from '../types';
import { useBonusesFormatter } from '@theme/js/hooks/swiss/useBonusFormatter';
import { useMyPromotionsCancelBonusPopup } from '@theme/js/hooks/swiss/useMyPromotionsCancelBonusPopup';
import * as actions from '../store/actions';
import { useFetchFreeSpins } from './useFetchFreeSpins';
import { prepareMyPromotionBonusData } from '@theme/js/utils/prepareMyPromotionBonusData';
import { IMoneyInfo } from '@core/js/hooks/socket/types';

const FREE_SPINS_SCROLL_KEY = 'FREE_SPINS_SCROLL_KEY';

export const useTableData = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const showTermsPopup = useShowTermsPopup();
    const fetchFreeSpins = useFetchFreeSpins();
    const showCancelBonusPopup = useMyPromotionsCancelBonusPopup({
        actions,
        onSuccess: fetchFreeSpins,
        key: FREE_SPINS_SCROLL_KEY
    });
    const {
       freeSpinsBalance, bonusDetails
    } = useDynamicReducerSelector(pick(['freeSpinsBalance', 'bonusDetails']));

    const formatCurrency = useCallback(
        ({ amount, currencyCode } : { amount: number; currencyCode: string }) =>
            formatter.formatCurrency(amount, { currencyCode }),
        [formatter]
    ) as unknown as (data: IMoneyInfo) => string;

    const transformedData = useMemo(
        () => prepareMyPromotionBonusData(freeSpinsBalance, bonusDetails),
        [bonusDetails, freeSpinsBalance]);

    const bonuses = useBonusesFormatter(transformedData);

    const headerCellsCreator = useMemo(() => getHeaderCellsCreator({
        style,
        translate
    }),
        [translate]
    );

    const rowCellsCreator = useMemo(() => getRowCellsCreator({
        showCancelBonusPopup,
        showTermsPopup,
        style,
        translate
    }), [showCancelBonusPopup, showTermsPopup, translate]);

    const dataFormatter = useMemo(() => getFormattedData({
            translate,
            formatCurrency
        }),
        [formatCurrency, translate]
    );

    const tableAccordionDataCreator = useMemo(() => getTableAccordionDataCreator({
        pathToId: 'referenceId',
        createHeaderCells: headerCellsCreator,
        createRowCells: pipe(
            dataFormatter,
            data => data.filter(pipe(prop('value'), Boolean)),
            map(rowCellsCreator)) as unknown as (item: IBonusDetailsItem) =>
                { id: number; content: ReactNode; className?: string | undefined }[]
    }), [dataFormatter, headerCellsCreator, rowCellsCreator]);

    return useMemo(() => tableAccordionDataCreator(
        bonuses as unknown as IBonusDetailsItem[]
    ), [bonuses, tableAccordionDataCreator]);
};
